import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Copy, Eye, Trash2, Plus, AlertCircle, CheckCircle2 } from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvestorAdmin() {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', firm: '', note: '' });
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState(null);
  const [error, setError] = useState('');
  const [viewLog, setViewLog] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/owner/investors`);
      setInvites(data);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to load invites'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setError(''); setLastCreated(null);
    try {
      const { data } = await axios.post(`${API}/owner/investors`, form);
      const fullUrl = `${window.location.origin}${data.share_path}`;
      setLastCreated({ ...data, fullUrl });
      setForm({ name: '', email: '', firm: '', note: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invite');
    } finally { setCreating(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this invite? The investor will lose access immediately.')) return;
    try {
      await axios.delete(`${API}/owner/investors/${id}`);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const loadViewLog = async (id) => {
    try {
      const { data } = await axios.get(`${API}/owner/investors/${id}/views`);
      setViewLog({ id, events: data });
    } catch (err) { logger.error('view log', err); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      window.alert('Link copied to clipboard. Share it with the investor.');
    }).catch(() => {
      window.prompt('Copy this link:', text);
    });
  };

  return (
    <div className="space-y-6" data-testid="investor-admin">
      <h2 className="text-2xl font-semibold tracking-tight">Investor Data Room</h2>

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-5">
        <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4 text-blue-500" /> Issue new invite
        </h3>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input required placeholder="Investor name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm" />
          <input required placeholder="Firm (e.g., Acme Capital)" value={form.firm} onChange={(e) => setForm({ ...form, firm: e.target.value })} data-testid="invite-firm" className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm" />
          <input placeholder="Internal note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm" />
          <button type="submit" disabled={creating} data-testid="invite-create-btn" className="md:col-span-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40">
            {creating ? 'Creating…' : 'Generate access link'}
          </button>
        </form>
        {error && <p className="mt-3 text-xs text-rose-500 flex items-center gap-1" data-testid="invite-error"><AlertCircle className="w-3 h-3" />{error}</p>}
        {lastCreated && (
          <div className="mt-4 p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50" data-testid="invite-created-success">
            <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1 mb-2">
              <CheckCircle2 className="w-3 h-3" /> Link generated — copy now, it won't be shown again
            </p>
            <div className="flex gap-2">
              <input readOnly value={lastCreated.fullUrl} className="flex-1 px-3 py-2 rounded-md border border-emerald-200 dark:border-emerald-900/50 bg-white dark:bg-slate-900 text-xs font-mono" />
              <button onClick={() => copy(lastCreated.fullUrl)} data-testid="copy-link-btn" className="px-3 py-2 rounded-md bg-emerald-600 text-white text-xs hover:bg-emerald-700 flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-100 dark:border-slate-800">
          <h3 className="text-base font-semibold">Issued invites ({invites.length})</h3>
        </header>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <th className="px-4 py-2">Investor</th>
                <th className="px-4 py-2">Firm</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Views</th>
                <th className="px-4 py-2">Last viewed</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {invites.map((i) => {
                const statusColor = {
                  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                  agreed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
                  revoked: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
                }[i.status] || 'bg-slate-100';
                return (
                  <tr key={i.id} data-testid={`invite-row-${i.id}`}>
                    <td className="px-4 py-2">
                      <div className="text-slate-900 dark:text-slate-50">{i.name}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{i.email}</div>
                    </td>
                    <td className="px-4 py-2 text-slate-700 dark:text-slate-200">{i.firm}</td>
                    <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${statusColor}`} data-testid={`status-${i.id}`}>{i.status}</span></td>
                    <td className="px-4 py-2 text-right font-mono text-slate-700 dark:text-slate-200">{i.view_count || 0}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                      {i.last_viewed_at ? new Date(i.last_viewed_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 flex gap-1 justify-end">
                      <button onClick={() => loadViewLog(i.id)} data-testid={`view-log-${i.id}`} className="text-xs p-1 text-slate-500 hover:text-blue-600" title="View access log">
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                      {i.status !== 'revoked' && (
                        <button onClick={() => revoke(i.id)} data-testid={`revoke-${i.id}`} className="text-xs p-1 text-slate-500 hover:text-rose-600" title="Revoke access">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {invites.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No invites issued yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {viewLog && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4" onClick={() => setViewLog(null)} role="presentation">
          <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" data-testid="view-log-modal">
            <header className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex justify-between">
              <h3 className="font-semibold">Access log</h3>
              <button onClick={() => setViewLog(null)} className="text-slate-400">✕</button>
            </header>
            <div className="p-5">
              {viewLog.events.length === 0 ? <p className="text-sm text-slate-500">No events yet.</p> : (
                <ul className="space-y-2">
                  {viewLog.events.map((e) => (
                    <li key={e.id} className="text-xs flex justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span><strong>{e.event}</strong>{e.section ? ` · ${e.section}` : ''}</span>
                      <span className="text-slate-500 font-mono">{e.ip} · {new Date(e.at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
